/**
 * Die Morgenmeditation abspielen — und erst beim „gehört" vermerken.
 *
 * 23.09.2026 (Ticket 2549): Der Player entsteht in app.ts
 * (`buildNarrationPlayer` lebt dort) und kommt in die Karte, die das Ereignis
 * `vespera:meditation-anhoeren` schickt. Ohne `text` im Body nimmt er den Weg
 * über den Endpunkt: der liefert die nachts fertig vertonte Datei — beim
 * Antippen wird nichts vertont, und die Leiste kennt Dauer und Sprünge.
 *
 * ⚠️ 27.09.2026 (Ticket 2636, Nachbesserung) — aus app.ts hierher gezogen.
 *    Die Meditation ist der sechste Weg auf der Tafel „Wege" und wird wie der
 *    Spiegel erst abgehakt, wenn der Player „gehört" meldet (eine Minute
 *    wirklich abgespielt, lib/hoerzeit.ts), nie beim Antippen. In app.ts war
 *    das nur als Text prüfbar: ein Vermerk gleich nach `play()` blieb im Test
 *    grün. Hier läuft der echte Hörer im Test, nur der Player ist gestellt.
 */
import { wienerTag } from "./tagesangebot.js";

/** Was dieser Weg vom Player-Bau in app.ts verlangt — nicht mehr. */
export interface MeditationsSpielerWunsch {
  titel: string;
  initialVoice: string;
  endpoint: string;
  getBody: () => Record<string, unknown>;
  /** Feuert EINMAL, sobald wirklich gehört (siehe buildNarrationPlayer). */
  onGehoert: () => void;
}

interface MeditationsSpieler {
  el: HTMLElement;
  setReady(bereit: boolean): void;
  play(): void;
}

/**
 * Die Meditation dieses Tages als gehört vermerken — und die Tafel erfährt es
 * sofort, ohne Neuladen (`voice-v8:tagesbild-frisch`).
 */
function vermerkeMeditationGehoert(tag: string): void {
  void fetch("/api/gelesen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ art: "meditation", schluessel: tag }),
  })
    .then((r) => {
      if (r.ok) window.dispatchEvent(new CustomEvent("voice-v8:tagesbild-frisch"));
    })
    .catch(() => undefined);
}

/**
 * Der Hörer für `vespera:meditation-anhoeren`. app.ts meldet ihn an und
 * reicht seinen Player-Bau und die Vorlesestimme herein.
 *
 * `detail.tag` ist der Tag der Karte (eine Meditation von gestern bleibt die
 * von gestern); ohne ihn gilt der heutige Wiener Tag — derselbe Schlüssel,
 * den die Tafel für „gehört" liest (server/tagesbild.ts).
 */
export function meditationAnhoeren(
  ev: Event,
  bauePlayer: (wunsch: MeditationsSpielerWunsch) => MeditationsSpieler,
  stimme: () => string,
): void {
  const d = (ev as CustomEvent<{ ort?: unknown; tag?: unknown }>).detail;
  if (!(d?.ort instanceof HTMLElement)) return;
  const tag = typeof d.tag === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.tag) ? d.tag : "";
  const gehoertTag = tag || wienerTag(new Date());
  const spieler = bauePlayer({
    titel: "Morgenmeditation",
    initialVoice: stimme(),
    endpoint: `/api/morgenmeditation/narrate${tag ? `?tag=${tag}` : ""}`,
    getBody: () => ({}),
    onGehoert: () => vermerkeMeditationGehoert(gehoertTag),
  });
  d.ort.replaceChildren(spieler.el);
  spieler.setReady(true);
  spieler.play();
}
